import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  getAdminDocuments,
  initAdminDocumentHistory,
  mergeDocuments,
  onFundingDocument,
  onSigningDocument,
  publishDocument,
  resetDeleteDocument,
  resetMergeDocuments,
  resetUpdateDocument,
  resetUploadDocumentStatus,
  unpublishDocument,
  updateDocumentMetadata,
  uploadDocuments,
} from 'store/order/documents/adminDocuments/dispatch';
import {
  setDocumentDetailsAction,
  setDocumentHistoryErrorAction,
  setDocumentHistoryFetchStatusAction,
} from 'store/order/documents/adminDocuments/reducer';
import { postGenerateLinkAction, postGenerateLinkSlice } from 'store/order/documents/slices';
import { RootStore } from 'store/types';
import { ICreateDocumentAccess } from 'types/documents/ICreateDocumentAccess';
import { eDocumentVisibilityType } from 'types/order/documents/eDocumentVisibilityType';
import { IDocumentCategory } from 'types/order/documents/IDocumentCategoryType';
import { IStandardDocumentData, IStandardDocumentVersionData } from 'types/order/documents/shared/interfaces';
import { IUploadDocument } from 'types/order/documents/upload/interfaces';
import { createDefaultFetch } from 'types/rest/IFetchStatus';
import { ErrorMessage, Grid, PageLoading } from 'ui-toolkit';
import { useSelectCurrentOrder } from '../../../selectors/order/selectCurrentOrder';
import { finalizeDocumentAction, finalizeDocumentSlice } from '../../../store/documents/slices';
import { isAmericanDigital } from '../../../utilities/order/hasAmericanDigitalUnderwriter';
import { StandardDocumentsPage } from './StandardDocumentsPage';
import {
  defaultNormalizedDocumentCategoriesData,
  useListDocumentCategoriesQuery,
} from '../../../API/documentCategories/DocumentCategoriesApi';
import { useSetOrderNavigationTab } from '../../../utilities/hooks/useSetOrderNavigationTab';
import { useParams } from 'react-router-dom';
import { UUID } from '../../../types/shared/UUID';
import { OrderTabs } from 'store/order/orderNavigation/orderNavigationSlice';
import { useAppDispatch } from '../../../store/store';

export const StandardDocumentsContainer: React.FC<{ isZDocLoading: boolean }> = ({ isZDocLoading }) => {
  useSetOrderNavigationTab({ tab: OrderTabs.Documents });
  const { orderId } = useParams<{ orderId: UUID }>();
  const { data: order } = useSelectCurrentOrder();
  const dispatch = useAppDispatch();
  const {
    documents,
    deleteDocumentsError,
    documentsError,
    documentsFetchStatus,
    deleteDocumentsFetchStatus,
    uploadDocumentsFetchStatus,
    editDocumentsFetchStatus,
    documentsActionError,
    documentsActionFetchStatus,
    documentDetails,
  } = useSelector((state: RootStore) => state.order.documents.adminDocuments);

  const { users, usersError, usersFetchStatus } = useSelector((state: RootStore) => state.usersCollection.allUsers);
  const { postGenerateLink } = useSelector((state: RootStore) => state.order.documents);
  const { data: { allCategories } = defaultNormalizedDocumentCategoriesData } = useListDocumentCategoriesQuery();

  useEffect(() => {
    if (documentDetails?.id && !documentDetails?.isRepeatable) {
      dispatch(initAdminDocumentHistory(documentDetails.id));
    }
  }, [dispatch, documentDetails?.id, documentDetails?.isRepeatable]);

  useEffect(() => {
    dispatch(getAdminDocuments(orderId, allCategories));
  }, [orderId, dispatch, allCategories]);

  useEffect(() => {
    if (!documentDetails) {
      return;
    }
    const updatedDocumentDetails = documents[orderId].find((t) => t.id === documentDetails.id);
    if (updatedDocumentDetails) {
      dispatch(setDocumentDetailsAction(updatedDocumentDetails));
    }
  }, [documents, dispatch, documentDetails, orderId]);

  // The page layout allows us to only show the "page" part of the page when we have success.
  // Error messaging will still show when the page isn't ready.
  const pageReady =
    documentsFetchStatus.success &&
    usersFetchStatus.success &&
    (!deleteDocumentsFetchStatus.fetched || deleteDocumentsFetchStatus.success);
  const pageError = documentsError || deleteDocumentsError || usersError;
  const onSigningClick = (documentId: string, versionId: string, value: boolean) =>
    dispatch(onSigningDocument(orderId, documentId, versionId, value));
  const onFundingClick = (documentId: string, versionId: string, value: boolean) =>
    dispatch(onFundingDocument(orderId, documentId, versionId, value));
  const onPublishDocument = (documentId: string, visibilityType: eDocumentVisibilityType) =>
    dispatch(publishDocument(orderId, documentId, visibilityType));
  const onUnpublishDocument = (documentId: string, visibilityType: eDocumentVisibilityType) =>
    dispatch(unpublishDocument(orderId, documentId, visibilityType));

  const onUploadDocuments = async (docsToUpload: IUploadDocument[]) => {
    try {
      await dispatch(uploadDocuments(orderId, docsToUpload, documents[orderId]));
      dispatch(getAdminDocuments(orderId, allCategories));
    } catch (e) {}
  };
  const onEditDocuments = async (document: IStandardDocumentData) => {
    await dispatch(updateDocumentMetadata(orderId, document));
  };

  const onUploadDocumentReset = () => {
    dispatch(resetUploadDocumentStatus(orderId));
  };

  const onEditDocumentReset = () => {
    dispatch(resetUpdateDocument(orderId));
  };

  const onDeleteDocumentReset = () => {
    dispatch(resetDeleteDocument());
  };

  const onMergeDocuments = async (docIds: string[], fileName: string | undefined, category: IDocumentCategory) =>
    dispatch(mergeDocuments(orderId, docIds, fileName, category));

  const onGenerateLink = async (createDocumentAccess: ICreateDocumentAccess) =>
    dispatch(postGenerateLinkAction(createDocumentAccess));

  const onResetGenerateLink = () => {
    dispatch(postGenerateLinkSlice.actions.reset({}));
  };

  const onCloseMergeDocuments = async () => dispatch(resetMergeDocuments(orderId));

  const onFinalizeDocument = (documentId: string, versionId: string) =>
    dispatch(finalizeDocumentAction({ documentId, versionId }));

  const onCloseFinalizeModal = () => dispatch(getAdminDocuments(orderId, allCategories));

  const resetFinalDocumentForm = () => dispatch(finalizeDocumentSlice.actions.reset({}));
  const onDetailsClick = (document: IStandardDocumentVersionData) => {
    dispatch(setDocumentDetailsAction(document));
  };
  const onCloseDetailsSideSheet = () => {
    dispatch(setDocumentDetailsAction(null));
    dispatch(setDocumentHistoryErrorAction(null));
    dispatch(setDocumentHistoryFetchStatusAction(createDefaultFetch()));
  };

  return (
    <Grid>
      <PageLoading isLoading={!(pageReady || pageError)} />
      <ErrorMessage message={pageError} />
      {pageReady && !pageError && (
        <StandardDocumentsPage
          isZDocLoading={isZDocLoading}
          categories={allCategories}
          isAmericanDigital={isAmericanDigital(order)}
          documents={documents[orderId] || []}
          users={users}
          parties={order ? order.parties : []}
          onUnpublish={onUnpublishDocument}
          onPublish={onPublishDocument}
          onSigningClick={onSigningClick}
          onFundingClick={onFundingClick}
          onUploadDocuments={onUploadDocuments}
          onUploadDocumentReset={onUploadDocumentReset}
          uploadDocumentFetch={uploadDocumentsFetchStatus}
          onEditDocument={onEditDocuments}
          onEditDocumentReset={onEditDocumentReset}
          onDeleteDocumentReset={onDeleteDocumentReset}
          editDocumentFetch={editDocumentsFetchStatus}
          onMergeDocuments={onMergeDocuments}
          onCloseMerge={onCloseMergeDocuments}
          documentsActionError={documentsActionError}
          documentsActionFetchStatus={documentsActionFetchStatus}
          onGenerateLink={onGenerateLink}
          postGenerateLink={postGenerateLink}
          onResetGenerateLink={onResetGenerateLink}
          onFinalizeDocument={onFinalizeDocument}
          resetFinalDocumentForm={resetFinalDocumentForm}
          onCloseFinalizeModal={onCloseFinalizeModal}
          onDetailsClick={onDetailsClick}
          onCloseDetailsSideSheet={onCloseDetailsSideSheet}
        />
      )}
    </Grid>
  );
};
